import React from 'react';
import { AiOutlineReload, AiOutlineHome, AiOutlineExclamationCircle } from 'react-icons/ai';

class ErrorBoundaryClass extends React.Component {
  constructor(props) {
    super(props);
    this.state = {
      hasError: false,
      error: null,
      errorInfo: null,
    };
  }

  static getDerivedStateFromError(error) {
    // Actualiza el estado para que el siguiente render muestre la UI de error
    return { hasError: true };
  }

  componentDidCatch(error, errorInfo) {
    // Registra el error para debugging
    console.error('ErrorBoundary capturó un error:', error, errorInfo);
    
    this.setState({
      error: error,
      errorInfo: errorInfo,
    });

    // Aquí podrías enviar el error a un servicio de logging externo
    // Ejemplo: logErrorToService(error, errorInfo);
  }

  handleReset = () => {
    this.setState({
      hasError: false,
      error: null,
      errorInfo: null,
    });
  };

  render() {
    if (this.state.hasError) {
      // Renderiza la UI de error personalizada
      return (
        <ErrorFallback
          error={this.state.error}
          errorInfo={this.state.errorInfo}
          onReset={this.handleReset}
          fallbackTitle={this.props.fallbackTitle}
          fallbackMessage={this.props.fallbackMessage}
        />
      );
    }

    return this.props.children;
  }
}

// Componente funcional para la UI de error
const ErrorFallback = ({ error, errorInfo, onReset, fallbackTitle, fallbackMessage }) => {
  const isDevelopment = process.env.NODE_ENV === 'development';
  const [showDetails, setShowDetails] = React.useState(false);

  const handleGoHome = () => {
    onReset();
    // Usar window.location para que funcione en todos los contextos
    window.location.href = '/';
  };

  return (
    <div style={styles.container}>
      <div style={styles.card}>
        <div style={styles.iconContainer}>
          <AiOutlineExclamationCircle size={64} color="#DC2626" />
        </div>
        
        <h1 style={styles.title}>
          {fallbackTitle || 'Algo salió mal'}
        </h1>
        
        <p style={styles.message}>
          {fallbackMessage || 
            'Ocurrió un error inesperado. Por favor, intenta nuevamente o vuelve al inicio.'}
        </p>

        <div style={styles.buttonGroup}>
          <button
            onClick={onReset}
            style={styles.primaryButton}
            title="Reintentar"
          >
            <AiOutlineReload size={20} />
            Reintentar
          </button>
          
          <button
            onClick={handleGoHome}
            style={styles.secondaryButton}
            title="Volver al inicio"
          >
            <AiOutlineHome size={20} />
            Volver al inicio
          </button>
        </div>

        {isDevelopment && error && (
          <div style={styles.detailsContainer}>
            <button
              onClick={() => setShowDetails(!showDetails)}
              style={styles.detailsButton}
            >
              {showDetails ? 'Ocultar' : 'Mostrar'} detalles técnicos
            </button>
            
            {showDetails && (
              <div style={styles.details}>
                <h3 style={styles.detailsTitle}>Detalles del error:</h3>
                <pre style={styles.errorText}>
                  {error.toString()}
                </pre>
                {errorInfo && (
                  <>
                    <h4 style={styles.detailsSubtitle}>Stack trace:</h4>
                    <pre style={styles.stackTrace}>
                      {errorInfo.componentStack}
                    </pre>
                  </>
                )}
              </div>
            )}
          </div>
        )}
      </div>
    </div>
  );
};

const styles = {
  container: {
    display: 'flex',
    justifyContent: 'center',
    alignItems: 'center',
    minHeight: '100vh',
    padding: '20px',
    backgroundColor: '#F8F9FA',
  },
  card: {
    backgroundColor: '#FFFFFF',
    borderRadius: '12px',
    padding: '40px',
    maxWidth: '600px',
    width: '100%',
    boxShadow: '0 4px 6px rgba(0, 0, 0, 0.1)',
    textAlign: 'center',
  },
  iconContainer: {
    display: 'flex',
    justifyContent: 'center',
    marginBottom: '24px',
  },
  title: {
    fontSize: '28px',
    fontWeight: 'bold',
    color: '#111827',
    margin: '0 0 16px 0',
  },
  message: {
    fontSize: '16px',
    color: '#6B7280',
    margin: '0 0 32px 0',
    lineHeight: '1.6',
  },
  buttonGroup: {
    display: 'flex',
    gap: '12px',
    justifyContent: 'center',
    flexWrap: 'wrap',
  },
  primaryButton: {
    display: 'flex',
    alignItems: 'center',
    gap: '8px',
    backgroundColor: '#00468B',
    color: '#FFFFFF',
    padding: '12px 24px',
    borderRadius: '6px',
    border: 'none',
    cursor: 'pointer',
    fontSize: '16px',
    fontWeight: '600',
    transition: 'background-color 0.2s ease',
  },
  secondaryButton: {
    display: 'flex',
    alignItems: 'center',
    gap: '8px',
    backgroundColor: '#F3F4F6',
    color: '#374151',
    padding: '12px 24px',
    borderRadius: '6px',
    border: '1px solid #D1D5DB',
    cursor: 'pointer',
    fontSize: '16px',
    fontWeight: '600',
    transition: 'background-color 0.2s ease',
  },
  detailsContainer: {
    marginTop: '32px',
    paddingTop: '32px',
    borderTop: '1px solid #E5E7EB',
  },
  detailsButton: {
    backgroundColor: 'transparent',
    color: '#6B7280',
    padding: '8px 16px',
    borderRadius: '6px',
    border: '1px solid #D1D5DB',
    cursor: 'pointer',
    fontSize: '14px',
    marginBottom: '16px',
  },
  details: {
    textAlign: 'left',
    backgroundColor: '#F9FAFB',
    padding: '16px',
    borderRadius: '6px',
    border: '1px solid #E5E7EB',
  },
  detailsTitle: {
    fontSize: '16px',
    fontWeight: 'bold',
    color: '#111827',
    margin: '0 0 12px 0',
  },
  detailsSubtitle: {
    fontSize: '14px',
    fontWeight: '600',
    color: '#374151',
    margin: '16px 0 8px 0',
  },
  errorText: {
    fontSize: '12px',
    color: '#DC2626',
    backgroundColor: '#FFFFFF',
    padding: '12px',
    borderRadius: '4px',
    overflow: 'auto',
    margin: '0 0 16px 0',
    fontFamily: 'monospace',
    whiteSpace: 'pre-wrap',
    wordBreak: 'break-word',
  },
  stackTrace: {
    fontSize: '11px',
    color: '#6B7280',
    backgroundColor: '#FFFFFF',
    padding: '12px',
    borderRadius: '4px',
    overflow: 'auto',
    maxHeight: '300px',
    fontFamily: 'monospace',
    whiteSpace: 'pre-wrap',
    wordBreak: 'break-word',
  },
};

// Wrapper para usar con hooks de React Router
const ErrorBoundary = (props) => {
  return <ErrorBoundaryClass {...props} />;
};

export default ErrorBoundary;

